import { RefreshCw, RotateCw, ArrowRight } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { Sparkline } from "../../components/Sparkline";
import { useToast } from "../../components/Toast";
import { useApiFallback } from "../../hooks/useApiFallback";
import {
  getLocalStatus,
  getLocalServices,
  triggerLocalSync,
} from "../../api/endpoints";
import { ApiError } from "../../api/types";
import type { LocalServiceOut, LocalStatusOut } from "../../api/types";
import { NODE_INFO, localServices as mockServices, nodeLog } from "../../data";
import { useNavigate } from "react-router-dom";

// Fallback на случай недоступности API. Снимок состояния и метрик —
// тот же набор, что был раньше в data.ts, только в форме backend-схемы.
const FALLBACK_STATUS: LocalStatusOut = {
  local_id: NODE_INFO.id,
  status: "online",
  uptime_seconds: 12 * 86400 + 4 * 3600,
  generation: NODE_INFO.generation,
  last_sync_min: NODE_INFO.syncMinutesAgo,
  peers_count: 4,
  resources: {
    cpu_percent: 12.4,
    mem_percent: 48.7,
    mem_used_mb: 3120,
    mem_total_mb: 8192,
  },
};

const FALLBACK_SERVICES: LocalServiceOut[] = mockServices.map((s) => ({
  name: s.id,
  status: s.status === "running" ? "running" : s.status === "down" ? "failed" : "running",
  pid: null,
  description: s.description,
}));

export default function StatusPage() {
  const navigate = useNavigate();
  const push = useToast();
  const qc = useQueryClient();

  // Авто-обновление каждые 10 секунд — статус «живой» панели.
  const statusQ = useQuery({
    queryKey: ["local", "status"],
    queryFn: getLocalStatus,
    refetchInterval: 10_000,
  });
  const servicesQ = useQuery({
    queryKey: ["local", "services"],
    queryFn: getLocalServices,
    refetchInterval: 15_000,
  });

  const status = useApiFallback<LocalStatusOut>(statusQ.data, statusQ.isError, FALLBACK_STATUS);
  const services = useApiFallback<LocalServiceOut[]>(servicesQ.data, servicesQ.isError, FALLBACK_SERVICES);

  const syncMut = useMutation({
    mutationFn: triggerLocalSync,
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["local", "status"] });
      push({ kind: "success", text: res?.message ?? "Синхронизация запрошена" });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : "Не удалось запустить синхронизацию";
      push({ kind: "warn", text: msg });
    },
  });

  const st = status.value;
  const uptimeStr = formatUptime(st.uptime_seconds);

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Состояние узла
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            {NODE_INFO.location} · {st.local_id} · поколение {st.generation}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Btn
            icon={RefreshCw}
            onClick={() => syncMut.mutate()}
            iconClass={syncMut.isPending ? "spin" : ""}
          >
            {syncMut.isPending ? "Синхронизация…" : "Запустить синхронизацию"}
          </Btn>
          <Btn icon={RotateCw} onClick={() => navigate("/local/services")}>
            Открыть сервисы
          </Btn>
        </div>
      </header>

      {/* 4 метрики */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label="Аптайм"
          value={<span>{uptimeStr}</span>}
          sub={`статус ${st.status}`}
          subRow={
            <div className="mt-1 font-mono text-[11px] text-[var(--color-fg-faint)]">
              uptime {st.uptime_seconds.toLocaleString("ru-RU")} с
            </div>
          }
        />
        <Metric
          label="CPU"
          accent
          value={<span>{st.resources.cpu_percent.toFixed(1)}<span className="text-[20px] text-[var(--color-fg-faint)] font-normal"> %</span></span>}
          subRow={
            <>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-surface-3)]">
                <div className="h-full rounded-full bg-[var(--color-brand-2)]" style={{ width: `${Math.min(100, st.resources.cpu_percent)}%` }} />
              </div>
              <div className="mt-1 font-mono text-[11px] text-[var(--color-fg-faint)]">
                нагрузка процесса локала
              </div>
            </>
          }
        />
        <Metric
          label="Память"
          value={<><span>{st.resources.mem_percent.toFixed(1)}</span><span className="text-[20px] text-[var(--color-fg-faint)] font-normal"> %</span></>}
          sub={`${st.resources.mem_used_mb.toLocaleString("ru-RU")} / ${st.resources.mem_total_mb.toLocaleString("ru-RU")} MB`}
          subRow={
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-surface-3)]">
              <div className="h-full rounded-full bg-[var(--color-brand-2)]" style={{ width: `${Math.min(100, st.resources.mem_percent)}%` }} />
            </div>
          }
        />
        <Metric
          label="Последняя синхронизация"
          value={<><span>{st.last_sync_min}</span><span className="text-[20px] text-[var(--color-fg-faint)] font-normal"> мин назад</span></>}
          sub={`поколение ${st.generation} · ${st.peers_count} соседей`}
          subRow={<Sparkline values={[5,4,4,3,3,4,3,3,3,3]} color="var(--color-success)" className="mt-2 h-7 w-full" />}
        />
      </div>

      {/* Соседи + сервисы */}
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <NeighborsCard peersCount={st.peers_count} />
        <ServicesCard services={services.value} loading={servicesQ.isLoading} />
      </div>

      {/* Последние действия — пока из mock-журнала (журнал тянем на LogPage) */}
      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
            Последние действия узла
          </h2>
          <a href="/local/log" className="inline-flex items-center gap-1 text-[12px] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]">
            журнал узла <ArrowRight size={12} />
          </a>
        </div>
        <ul className="mt-3 divide-y divide-[var(--color-border)]">
          {nodeLog.slice(0, 5).map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-3 py-2 text-[12.5px]">
              <div className="flex min-w-0 items-center gap-3">
                <span className="w-14 shrink-0 font-mono text-[11px] text-[var(--color-fg-muted)]">{e.time}</span>
                <LevelChip level={e.level} />
                <span className="truncate text-[var(--color-fg)]">{e.text}</span>
              </div>
              <span className="shrink-0 font-mono text-[11px] text-[var(--color-fg-muted)]">{e.source}</span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

// ─── вспомогательное ───

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d} д ${h} ч`;
  if (h > 0) return `${h} ч ${m} мин`;
  return `${m} мин`;
}

function Metric({
  label,
  value,
  sub,
  subRow,
  accent = false,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  subRow?: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
        {label}
      </div>
      <div
        className={
          "mt-2 text-[30px] font-semibold leading-none tracking-[-0.02em] " +
          (accent ? "text-[var(--color-brand)]" : "text-[var(--color-fg)]")
        }
      >
        {value}
      </div>
      {sub && <div className="mt-2 text-[11.5px] text-[var(--color-fg-muted)]">{sub}</div>}
      {subRow && <div className="mt-2 text-[11.5px]">{subRow}</div>}
    </div>
  );
}

function LevelChip({ level }: { level: "info" | "warn" | "err" }) {
  const map = {
    info: { bg: "var(--color-surface-3)", fg: "var(--color-fg-muted)", text: "info" },
    warn: { bg: "var(--color-warning-bg)", fg: "var(--color-warning)", text: "warn" },
    err:  { bg: "var(--color-danger-bg)",  fg: "var(--color-danger)",  text: "err" },
  };
  const m = map[level];
  return (
    <span
      className="shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] font-medium"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.text}
    </span>
  );
}

// ─── соседи (микро-диаграмма) ───

function NeighborsCard({ peersCount }: { peersCount: number }) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
        Соседние серверы
      </h2>
      <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
        Активных peer-каналов: {peersCount}
      </p>
      <div className="mt-3">
        <NeighborsDiagram />
      </div>
    </section>
  );
}

function NeighborsDiagram() {
  const W = 560, H = 240;
  const cx = W / 2;
  const peers = [
    { id: "msk-02", x: 0.2 * W, y: 0.78 * H, color: "#16a34a" },
    { id: "spb-01", x: 0.42 * W, y: 0.88 * H, color: "#16a34a" },
    { id: "kzn-01", x: 0.62 * W, y: 0.88 * H, color: "#16a34a" },
    { id: "ekb-01", x: 0.82 * W, y: 0.78 * H, color: "#16a34a" },
  ];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="block w-full">
      <defs>
        <pattern id="grid-n" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.7" fill="#cbd5e1" opacity="0.55" />
        </pattern>
      </defs>
      <rect x="0" y="0" width={W} height={H} fill="url(#grid-n)" rx="6" />
      <g>
        <circle cx={cx} cy={36} r={14} fill="#1f3a8a" />
        <text x={cx} y={40} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700" fontFamily="IBM Plex Sans">G</text>
        <text x={cx} y={62} textAnchor="middle" fontSize="10" fill="#475569" fontFamily="IBM Plex Mono">GLOBAL</text>
      </g>
      <g>
        <circle cx={cx} cy={130} r={14} fill="none" stroke="#1f3a8a" strokeWidth={2.5} />
        <circle cx={cx} cy={130} r={20} fill="none" stroke="#1f3a8a" strokeOpacity="0.18" strokeWidth="2" />
        <text x={cx} y={155} textAnchor="middle" fontSize="10" fill="#475569" fontFamily="IBM Plex Mono">msk-01 (вы)</text>
      </g>
      <line x1={cx} y1={50} x2={cx} y2={116} stroke="#1f3a8a" strokeOpacity="0.5" strokeWidth="1.4" strokeDasharray="4 4" />
      {peers.map((p) => (
        <g key={p.id}>
          <line x1={cx} y1={144} x2={p.x} y2={p.y - 12} stroke="#1f3a8a" strokeOpacity="0.35" strokeWidth="1.2" strokeDasharray="4 4" />
          <circle cx={p.x} cy={p.y - 12} r={9} fill="#fff" stroke={p.color} strokeWidth={2.5} />
          <text x={p.x} y={p.y + 10} textAnchor="middle" fontSize="10" fill="#475569" fontFamily="IBM Plex Mono">{p.id}</text>
        </g>
      ))}
    </svg>
  );
}

// ─── список сервисов ───

function ServicesCard({ services, loading }: { services: LocalServiceOut[]; loading: boolean }) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
        Сервисы
      </h2>
      <ul className={"mt-3 divide-y divide-[var(--color-border)] " + (loading ? "opacity-70" : "")}>
        {services.map((s) => (
          <li key={s.name} className="flex items-center justify-between gap-3 py-2.5 text-[12.5px]">
            <div className="flex items-center gap-2.5">
              <ServiceDot status={s.status} />
              <div>
                <span className="font-mono font-medium text-[var(--color-fg)]">{s.name}</span>
                {s.description && (
                  <span className="text-[var(--color-fg-muted)]"> · {s.description}</span>
                )}
              </div>
            </div>
            <div className="text-right">
              <span className="font-mono text-[var(--color-fg-muted)]">
                {s.pid !== null ? `pid ${s.pid}` : s.status}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ServiceDot({ status }: { status: string }) {
  const color =
    status === "running" ? "var(--color-success)" :
    status === "failed"   ? "var(--color-danger)" :
                           "var(--color-warning)";
  return <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />;
}
